import { type FC } from 'react';
import { FormattedMessage } from 'dibs-react-intl';
import { useFragment, graphql } from 'react-relay';
import classnames from 'classnames';

import { useHomePageContext } from '../HomePageContext';
import { HpSharedFeaturedModuleItem } from './HpSharedFeaturedModuleItem';
import { HpSharedFeaturedModuleCarousel } from './HpSharedFeaturedModuleCarousel';

import { type HpSharedFeaturedModuleContent_viewer$key } from './__generated__/HpSharedFeaturedModuleContent_viewer.graphql';
import { type HpSharedFeaturedModuleContent_user$key } from './__generated__/HpSharedFeaturedModuleContent_user.graphql';

import dibsCss from 'dibs-css';

const viewerFragment = graphql`
    fragment HpSharedFeaturedModuleContent_viewer on Viewer {
        ...HpSharedFeaturedModuleCarousel_viewer
        onSaleVerticals {
            ...HpSharedFeaturedModuleItem_onSaleVertical
        }
    }
`;

const userFragment = graphql`
    fragment HpSharedFeaturedModuleContent_user on User {
        ...HpSharedFeaturedModuleCarousel_user
    }
`;

export const HpSharedFeaturedModuleContent: FC<{
    viewer?: HpSharedFeaturedModuleContent_viewer$key | null;
    user?: HpSharedFeaturedModuleContent_user$key | null;
}> = ({ viewer: viewerRef, user: userRef }) => {
    const viewer = useFragment(viewerFragment, viewerRef);
    const user = useFragment(userFragment, userRef);

    const { isMobile } = useHomePageContext();

    const { onSaleVerticals } = viewer || {};

    if (!onSaleVerticals?.length) {
        return null;
    }

    return (
        <>
            <div
                className={classnames(dibsCss.bgBuyertertiary, dibsCss.textNoir, {
                    ['primary-viewport-width']: isMobile,
                    [dibsCss.mtLarge]: !isMobile,
                })}
            >
                <div
                    className={classnames({
                        ['primary-page-width']: isMobile,
                        [dibsCss.pyLarge]: isMobile,
                        [dibsCss.pyLarger]: !isMobile,
                        [dibsCss.pxLarge]: !isMobile,
                    })}
                >
                    {!isMobile && (
                        <div
                            className={classnames(
                                dibsCss.textCenter,
                                dibsCss.sassyFontHeaderLargeItalic
                            )}
                        >
                            <FormattedMessage
                                id="HpSharedFeaturedModuleVariant.title"
                                defaultMessage="Limited Time Savings"
                            />
                        </div>
                    )}
                    <div
                        className={classnames(dibsCss.textCenter, {
                            [dibsCss.mbMedium]: isMobile,
                            [dibsCss.sassyFontHeaderLarge]: isMobile,
                            [dibsCss.mbLarger]: !isMobile,
                            [dibsCss.textHeaderBannerSmallNormal]: !isMobile,
                        })}
                    >
                        <FormattedMessage
                            id="HpSharedFeaturedModuleVariant.subTitle"
                            defaultMessage="Sale: Up to 40% Off"
                        />
                    </div>

                    <div
                        className={classnames(
                            dibsCss.flex,
                            dibsCss.justifyBetween,
                            dibsCss.itemsStart,
                            dibsCss.gapSmall,
                            {
                                [dibsCss.flexWrap]: isMobile,
                            }
                        )}
                    >
                        {onSaleVerticals.map((onSaleVertical, index) => (
                            <HpSharedFeaturedModuleItem
                                key={index}
                                onSaleVertical={onSaleVertical}
                            />
                        ))}
                    </div>
                </div>
            </div>
            <HpSharedFeaturedModuleCarousel viewer={viewer} user={user} isMobile={isMobile} />
        </>
    );
};
